// components/PostSelector/PostSelectorModal.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { useAppStore } from "@/core/store/useAppStore";
import { usePostGrid } from "@/domains/Grid/hooks/usePostGrid";
import { PostItem } from "@/core/Post/PostItem.model";
import { PostGrid } from "./PostGrid";
import { UserRole } from '@/services/urlService';
import { usePostsQuery } from "@/domains/PostCreator/hooks/usePostsQuery";

export const PostSelectorModal: React.FC = () => {
  const ui = useAppStore((state) => state.ui);
  const setActiveModal = useAppStore((state) => state.setActiveModal);
  const selectedClient = useAppStore((state) => state.selectedClient);

  // Use React Query for posts data
  const { data: postItems = [], isLoading: isLoadingPosts, error: queryError } = usePostsQuery(selectedClient?.id);
  const postsError = queryError instanceof Error ? queryError.message : queryError ? String(queryError) : null;

  const { setPost } = usePostGrid();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPost, setSelectedPost] = useState<PostItem | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const isOpen = ui.activeModal?.name === "post-selector";
  const modalProps = ui.activeModal?.props || {};
  const { gridPosition, gridMode } = modalProps;

  // Get posts that can be in grid (filter from React Query data)
  const availablePosts = useMemo(
    () => postItems.filter((post: PostItem) => post.isGridEligible()),
    [postItems]
  );

  // Apply filters
  const filteredPosts = useMemo(
    () => availablePosts.filter((post: PostItem) => {
      // Status filter
      const matchesStatus =
        statusFilter === "all" || post.status === statusFilter;
      return matchesStatus;
    }),
    [availablePosts, statusFilter]
  );

  const handleClose = () => {
    setActiveModal(null);
    setSelectedPost(null);
    setSearchQuery("");
    setStatusFilter("all");
  };

  const handlePostSelect = (post: PostItem) => {
    setSelectedPost(post);
  };

  const handleSave = () => {
    if (selectedPost && typeof gridPosition === "number") {
      // Convert PostItem back to legacy Post format for the grid
      const legacyPost = selectedPost.toLegacyPost();
      setPost(gridPosition, legacyPost);
      console.log("Added post to grid:", {
        postId: selectedPost.id,
        position: gridPosition,
        gridMode,
      });
    }
    handleClose();
  };

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedPost(null);
      setSearchQuery("");
      setStatusFilter("all");
    }
  }, [isOpen]);

  // Guard: Require selected client (after all hooks)
  if (isOpen && !selectedClient) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="No Client Selected"
        size="md"
      >
        <div className="p-6 text-center">
          <p className="text-gray-600 mb-4">Please select a client before selecting posts.</p>
          <Button onClick={handleClose}>Close</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Select Post for Grid"
      size="xl"
      className="max-w-6xl"
    >
      <div className="flex flex-col h-[80vh]">
        {/* Header */}
        <div className="p-6 border-b space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Select Post for Position {(gridPosition || 0) + 1}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Grid Mode: {gridMode} • {filteredPosts.length} posts available
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex items-center gap-4">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">
                Status:
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="draft">Draft</option>
                <option value="ready">Ready</option>
                <option value="scheduled">Scheduled</option>
                <option value="partially_published">Partially Published</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Info */}
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div>
              Showing {filteredPosts.length} of {availablePosts.length}{" "}
              grid-eligible posts
            </div>
            <div>
              {selectedPost && (
                <span className="text-blue-600 font-medium">
                  "{selectedPost.id}" selected
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <div className="p-6 overflow-y-auto h-full">
            {postsError ? (
              <div className="flex items-center justify-center py-12 text-red-600">
                <p>Error loading posts: {postsError}</p>
              </div>
            ) : (
              <PostGrid
                posts={filteredPosts.map(post => post.toLegacyPost())}
                selectedPost={selectedPost?.toLegacyPost() || null}
                onPostSelect={(legacyPost) => {
                  // selectedClient is guaranteed to exist due to guard above
                  const postItem = PostItem.fromLegacyPost(legacyPost, UserRole.ADMIN, selectedClient!.id);
                  handlePostSelect(postItem);
                }}
                isLoading={isLoadingPosts}
                error={postsError}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-between">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>

          <div className="flex space-x-2">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!selectedPost}
            >
              Add to Grid
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
